// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, { useEffect, useRef, useState } from "react";
import { useLogger } from "amazon-chime-sdk-component-library-react";
import { useUserFullname } from "@platform/hooks/useUserFullname";
import { useDevices } from "../../../providers/DevicesProvider";
import PreviewVideoTile from "./PreviewVideoTile";
import { useVideo } from "@platform/features/video/providers/VideoContext";
import { extractRoomMemberData } from "@platform/features/video/utils/roomMemberDataUtils";
import { streamTracker } from "@platform/features/video/utils/streamTracker";

export const PreviewVideo: React.FC<React.PropsWithChildren> = (props) => {
  const logger = useLogger();
  const videoEl = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const { selectedVideoInputDevice } = useDevices();

  const userFullname = useUserFullname();

  const { userName, roomMembersData } = useVideo();

  const { roomMemberData } = userName
    ? extractRoomMemberData({ externalUserId: userName }, roomMembersData)
    : { roomMemberData: null };

  // Cleanup function to stop all tracks
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  // Handle video preview when device changes
  useEffect(() => {
    async function startPreview(): Promise<void> {
      if (!selectedVideoInputDevice) {
        console.log("PreviewVideo: No selected video input device");
        return;
      }

      if (!videoEl.current) {
        console.log("PreviewVideo: Video element not ready, will retry");
        return;
      }

      console.log(
        "PreviewVideo: Starting preview with device:",
        selectedVideoInputDevice
      );

      try {
        // Stop any existing stream
        if (stream) {
          streamTracker.removeStream(stream);
          stream.getTracks().forEach((track) => track.stop());
        }

        // Get new stream with the selected device
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: selectedVideoInputDevice },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });

        console.log("PreviewVideo: Got new stream, setting to video element");

        // Set the stream to the video element
        if (videoEl.current) {
          videoEl.current.srcObject = newStream;
          // Ensure the video starts playing
          videoEl.current.play().catch((err) => {
            logger.error(`Error playing video: ${err}`);
          });

          // Track the new stream
          streamTracker.addStream(newStream);
          setStream(newStream);
          console.log("PreviewVideo: Stream set successfully");
        }
      } catch (error) {
        logger.error(`Failed to start video preview: ${error}`);
        console.error("PreviewVideo: Error starting preview:", error);
      }
    }

    // Add a small delay to ensure the device selection has been processed
    const timeoutId = setTimeout(() => {
      startPreview();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedVideoInputDevice, logger]);

  // Handle video element becoming available
  useEffect(() => {
    if (videoEl.current && selectedVideoInputDevice && !stream) {
      console.log("PreviewVideo: Video element available, triggering preview");
      // Trigger the preview effect by calling the function directly
      const startPreview = async () => {
        try {
          const newStream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: { exact: selectedVideoInputDevice },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          });

          if (videoEl.current) {
            videoEl.current.srcObject = newStream;
            videoEl.current.play().catch((err) => {
              logger.error(`Error playing video: ${err}`);
            });

            // Track the new stream
            streamTracker.addStream(newStream);
            setStream(newStream);
            console.log(
              "PreviewVideo: Stream set on video element availability"
            );
          }
        } catch (error) {
          logger.error(`Failed to start video preview: ${error}`);
        }
      };

      // Add a small delay to ensure everything is ready
      const timeoutId = setTimeout(() => {
        startPreview();
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [videoEl.current, selectedVideoInputDevice, stream, logger]);

  return (
    <div className="w-full">
      <PreviewVideoTile
        {...props}
        ref={videoEl}
        className="h-full w-full"
        nameplate={userFullname}
        roomMemberData={roomMemberData}
      />
    </div>
  );
};

export default PreviewVideo;
